import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSuccessionTheme } from '../../SuccessionThemeProvider';
import numeral from 'numeral';
import { currencySymbol } from '../../../../utils/formatters';
import { isSameUser } from '../shared/identity';

/**
 * PartialSaleInfo - Display partial sale details in chat conversations
 * 
 * Matches the legacy PartialSaleBoxInMessagesPage format and information
 * Full width with all details shown
 */
const PartialSaleInfo = ({ partialSaleListingUser, currentUser }) => {
  const intl = useIntl();
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();

  if (!partialSaleListingUser?.partial_sale_listing) {
    return null;
  }

  const listing = partialSaleListingUser.partial_sale_listing;
  const buyer = partialSaleListingUser.buyer;
  const seller = partialSaleListingUser.seller;
  const isCurrentUserSeller = isSameUser(currentUser?.id, seller?.id);

  // Company feature flags (like legacy component)
  const partialSalesPropertyCasualty = currentUser?.company?.feature_types?.includes('partial_sales_property_casualty') || false;
  const hideProductSalesPurchasePrice = currentUser?.company?.feature_types?.includes('hide_partial_sales_purchase_price') || false;

  const formatCurrency = (amount) => {
    const sym = currencySymbol();
    if (!amount || amount === 0) return `${sym}0.00`;
    return `${sym}${numeral(amount).format('0,0.00')}`;
  };

  const formatAskingPrice = (price) => {
    if (!price || price === '0.0' || price === '0' || price === 0 || parseFloat(price) === 0) return intl.formatMessage({ id: 'succession.messaging.partial_sale_info.negotiable' });
    return `${currencySymbol()}${numeral(price).format('0,0.00')}`;
  };

  const formatNumber = (number) => {
    if (!number || number === 0) return '0';
    return numeral(number).format('0,0');
  };

  // Calculate total revenue like marketplace cards
  const getTotalRevenue = (listing) => {
    const feeBasedRevenue = parseFloat(listing.fee_based_revenue) || 0;
    const trailsRevenue = parseFloat(listing.trails_revenue) || 0;
    const directRevenue = parseFloat(listing.direct_revenue) || 0;
    const commissionRevenue = parseFloat(listing.commission_revenue) || 0;
    return feeBasedRevenue + trailsRevenue + directRevenue + commissionRevenue;
  };

  // Status display function like marketplace cards
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'open':
        return { label: intl.formatMessage({ id: 'succession.messaging.partial_sale_info.status_published' }), color: 'bg-succession-success-bg text-succession-success border border-succession-success' };
      case 'new':
        return { label: intl.formatMessage({ id: 'succession.messaging.partial_sale_info.status_draft' }), color: 'bg-succession-bg-secondary text-succession-text-secondary border border-succession-border' };
      case 'closed':
        return { label: intl.formatMessage({ id: 'succession.messaging.partial_sale_info.status_closed' }), color: 'bg-succession-error-bg text-succession-error border border-succession-error' };
      default:
        return { label: intl.formatMessage({ id: 'succession.messaging.partial_sale_info.status_unknown' }), color: 'bg-succession-bg-secondary text-succession-text-secondary border border-succession-border' };
    }
  };

  return (
    <div className="border border-succession-border rounded-lg overflow-hidden mb-4 transition-colors duration-300 bg-succession-bg-primary" style={{ width: '100%' }}>
      
      {/* Header with Company Partial Sale Name */}
      <div className="px-4 py-3 text-center transition-colors duration-300 bg-succession-bg-secondary">
        <h3 className="font-medium text-sm transition-colors duration-300 text-succession-text-primary">
          {intl.formatMessage({ id: 'succession.messaging.partial_sale_info.conversation_title' }, { name: currentUser?.company?.partial_sale_name || intl.formatMessage({ id: 'succession.messaging.partial_sale_info.partial_sale_default' }) })}
        </h3>
      </div>

      {/* Main Content */}
      <div className="p-6">
        {/* Header Section - Like marketplace cards */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1 text-succession-text-primary transition-colors duration-300">
              {listing.segment_details || intl.formatMessage({ id: 'succession.messaging.partial_sale_info.practice_segment' })}
            </h3>
            <div className="flex items-start text-sm text-succession-text-secondary transition-colors duration-300">
              <svg className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="break-words">
                {listing.states?.join(', ') || intl.formatMessage({ id: 'succession.messaging.partial_sale_info.location_not_specified' })}
              </span>
            </div>
          </div>
          <div className="text-right ml-4">
            <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.status_label' })}</div>
            <div className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusDisplay(listing.sale_status).color}`}>
              {getStatusDisplay(listing.sale_status).label}
            </div>
          </div>
        </div>

        {/* Key Metrics - Like marketplace cards */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.clients_label' })}</div>
            <div className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
              {formatNumber(listing.number_of_clients)}
            </div>
          </div>
          <div>
            <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.revenue_label' })}</div>
            <div className="text-lg font-semibold text-succession-success">
              {formatCurrency(getTotalRevenue(listing))}
            </div>
          </div>
          {!hideProductSalesPurchasePrice && (
            <div className="col-span-2">
              <div className="text-sm text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.asking_price_label' })}</div>
              <div className="text-lg font-semibold text-succession-primary">
                {formatAskingPrice(listing.asking_price)}
              </div>
            </div>
          )}
        </div>

        {/* Description - Like marketplace cards */}
        {listing.description && (
          <div className="mb-4">
            <p className="text-sm text-succession-text-secondary transition-colors duration-300 leading-relaxed">
              {listing.description}
            </p>
          </div>
        )}

        {/* Revenue Breakdown - Like marketplace cards */}
        <div className="mb-4">
          <div className="text-sm font-medium mb-2 text-succession-text-primary transition-colors duration-300">
            {intl.formatMessage({ id: 'succession.messaging.partial_sale_info.revenue_breakdown' })}
          </div>
          <div className="space-y-1 text-xs">
            {listing.fee_based_revenue > 0 && (
              <div className="flex justify-between">
                <span className="text-succession-text-secondary transition-colors duration-300">
                  {partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.messaging.partial_sale_info.recurring_commission' }) : intl.formatMessage({ id: 'succession.messaging.partial_sale_info.fee_based' })}
                </span>
                <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.fee_based_revenue)}</span>
              </div>
            )}
            {!partialSalesPropertyCasualty && listing.trails_revenue > 0 && (
              <div className="flex justify-between">
                <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.trails' })}</span>
                <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.trails_revenue)}</span>
              </div>
            )}
            {!partialSalesPropertyCasualty && listing.direct_revenue > 0 && (
              <div className="flex justify-between">
                <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.direct' })}</span>
                <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.direct_revenue)}</span>
              </div>
            )}
            {listing.commission_revenue > 0 && (
              <div className="flex justify-between">
                <span className="text-succession-text-secondary transition-colors duration-300">
                  {partialSalesPropertyCasualty ? intl.formatMessage({ id: 'succession.messaging.partial_sale_info.property_casualty' }) : intl.formatMessage({ id: 'succession.messaging.partial_sale_info.commission' })}
                </span>
                <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.commission_revenue)}</span>
              </div>
            )}
            {partialSalesPropertyCasualty && listing.assets_under_management > 0 && (
              <div className="flex justify-between">
                <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.total_premium' })}</span>
                <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.assets_under_management)}</span>
              </div>
            )}
            {!partialSalesPropertyCasualty && listing.assets_under_management > 0 && (
              <div className="flex justify-between">
                <span className="text-succession-text-secondary transition-colors duration-300">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.aum' })}</span>
                <span className="text-succession-text-primary transition-colors duration-300">{formatCurrency(listing.assets_under_management)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Additional Details - Like marketplace cards */}
        {(listing.client_demographics_details || listing.services_provided_details || listing.other_details) && (
          <div>
            <h4 className="font-medium text-succession-text-primary mb-2 transition-colors duration-300">
              {intl.formatMessage({ id: 'succession.messaging.partial_sale_info.more_details' })}
            </h4>
            <div className="bg-succession-bg-secondary rounded-lg p-3 text-sm max-h-40 overflow-y-auto transition-colors duration-300 space-y-2">
              {listing.client_demographics_details && (
                <div>
                  <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.client_demographics' })}</span>
                  <p className="text-succession-text-primary">{listing.client_demographics_details}</p>
                </div>
              )}
              {listing.services_provided_details && (
                <div>
                  <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.services_provided' })}</span>
                  <p className="text-succession-text-primary">{listing.services_provided_details}</p>
                </div>
              )}
              {listing.other_details && (
                <div>
                  <span className="font-medium text-succession-text-primary">{intl.formatMessage({ id: 'succession.messaging.partial_sale_info.other' })}</span>
                  <p className="text-succession-text-primary">{listing.other_details}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

PartialSaleInfo.propTypes = {
  partialSaleListingUser: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired
};

export default PartialSaleInfo;